const binanceSockets: Record<string, WebSocket> = {};
const lastCloseMap: Record<string, number> = {};
let EPsCache: any[] = [];
let currentPriceType: string = 'last'; // 기본값은 'last'
let resetCacheCallback: any = null; // ✅ TradingView 캐시 초기화 콜백

// ═══════════════════════════════════════════════════════════════════════════
// 🔧 차트 데이터 소스 설정 (Chart Data Source Configuration)
// ═══════════════════════════════════════════════════════════════════════════
//
// 데이터 소스 타입:
//   - 'backend' : 자체 백엔드 Chart API (권장, 모든 심볼 통합 관리)
//   - 'binance' : Binance Futures API 직접 호출 (폴백용)
//
// 환경변수:
//   - VITE_APP_CHART_URL : 백엔드 Chart API URL (예: http://localhost:7777)
//
// 전환 방법: CHART_DATA_SOURCE 값만 변경하면 됨
// ═══════════════════════════════════════════════════════════════════════════
type ChartDataSource = 'backend' | 'binance';

const CHART_DATA_SOURCE = 'backend' as ChartDataSource; // ✅ 자체 백엔드 Chart API 사용

// 데이터 소스별 설정
const DATA_SOURCE_CONFIG = {
  backend: {
    baseUrl: import.meta.env.VITE_APP_CHART_URL as string,
    historyEndpoint: '/chart/history',
  },
  binance: {
    baseUrl: 'https://fapi.binance.com',
    historyEndpoint: '/fapi/v1/klines',
  },
} as const;

export const CustomDatafeed = {
  init: (EPs: any[]) => {
    EPsCache = EPs || [];
  },

  setPriceType: (priceType: string) => {
    currentPriceType = priceType;
  },

  getPriceType: () => {
    return currentPriceType;
  },

  searchSymbols: (
    userInput: string,
    exchange: string,
    symbolType: string,
    onResultReadyCallback: (symbols: any[]) => void
  ) => {
    if (!EPsCache || EPsCache.length === 0) {
      console.warn('[searchSymbols] EPsCache is empty');
      onResultReadyCallback([]);
      return;
    }

    const filtered = EPsCache.filter((item) =>
      item.pairs.toLowerCase().includes(userInput.toLowerCase())
    ).map((item) => ({
      symbol: item.pairs,
      full_name: item.pairs,
      description: item.name || item.pairs,
      exchange: 'Piecrip',
      ticker: item.pairs,
      type: 'crypto',
    }));

    onResultReadyCallback(filtered);
  },

  onReady: (cb) => {
    setTimeout(
      () =>
        cb({
          supported_resolutions: [
            '1',
            '3',
            '5',
            '15',
            '30',
            '1h',
            '2h',
            '4h',
            '6h',
            '8h',
            '12h',
            '1d',
            '3d',
            '1w',
            '1M',
          ],
        }),
      0
    );
  },

  resolveSymbol: (symbolName, onSymbolResolvedCallback) => {
    // 슬래시 없는 형태로 정규화 (앱에서 BTCUSDT 형태로 전달됨)
    const symbolWithoutSlash = symbolName.replace('/', '').toUpperCase();

    // pricescaleMap: 슬래시 없는 형태로 키 정의
    const pricescaleMap: Record<string, number> = {
      'BTCUSDT': 10,
      '1000PEPEUSDT': 10000000,
      'PEPEUSDT': 10000000,
      'XRPUSDT': 10000,
      'BCHUSDT': 100,
      'ADAUSDT': 100000,
      'AVAXUSDT': 1000,
      'DOGEUSDT': 100000,
      'DOTUSDT': 1000,
      'UNIUSDT': 1000,
      'LINKUSDT': 1000,
      'TRXUSDT': 100000,
      'SUIUSDT': 10000,
      'ETHUSDT': 100,
      'BNBUSDT': 100,
      'SOLUSDT': 1000,
      'LTCUSDT': 100,
      'MATICUSDT': 100000,
      'SHIBUSDT': 100000000,
      'APTUSDT': 1000,
      'ARBUSDT': 10000,
      'OPUSDT': 10000,
    };

    // PEPE → 1000PEPE 변환
    const normalizedSymbol = symbolWithoutSlash === 'PEPEUSDT' ? '1000PEPEUSDT' : symbolWithoutSlash;

    // pricescale 결정 (기본값 10000 = 소수점 4자리)
    const pricescale = pricescaleMap[normalizedSymbol] || 10000;

    // 표시용 심볼 (슬래시 포함 형태로 변환)
    const displaySymbol = symbolName.includes('/') ? symbolName :
      symbolName.replace(/^(.+?)(USDT|BUSD|BTC|ETH)$/i, '$1/$2').toUpperCase();

    onSymbolResolvedCallback({
      name: displaySymbol,
      ticker: displaySymbol,
      type: 'crypto',
      session: '24x7',
      timezone: 'Asia/Seoul',
      exchange: 'Piecrip',
      minmov: 1,
      pricescale,
      has_intraday: true,
      supported_resolutions: [
        '1',
        '3',
        '5',
        '15',
        '30',
        '1h',
        '2h',
        '4h',
        '6h',
        '8h',
        '12h',
        '1d',
        '3d',
        '1w',
        '1M',
      ],
      volume_precision: 2,
      data_status: 'streaming',
    });
  },

  // 히스토리 데이터 URL 생성 (데이터 소스별)
  getHistoryUrl: (
    symbol: string,
    apiResolution: string,
    cursor: number,
    end: number,
    chunkSize: number,
    dataSource: ChartDataSource = CHART_DATA_SOURCE
  ) => {
    const config = DATA_SOURCE_CONFIG[dataSource];

    switch (dataSource) {
      case 'backend': {
        // 백엔드 Chart API (UDF 형식)
        const tvResolution = normalizeToTVResolution(apiResolution);
        return `${config.baseUrl}${config.historyEndpoint}?symbol=${symbol}&resolution=${tvResolution}&from=${Math.floor(cursor / 1000)}&to=${Math.floor(end / 1000)}`;
      }

      case 'binance': {
        // Binance Futures API - priceType에 따른 엔드포인트 변경
        const endpoint =
          currentPriceType === 'last'
            ? 'klines'
            : currentPriceType === 'mark'
              ? 'markPriceKlines'
              : 'indexPriceKlines';
        return `${config.baseUrl}/fapi/v1/${endpoint}?${currentPriceType === 'index' ? 'pair' : 'symbol'}=${symbol}&interval=${apiResolution}&startTime=${cursor}&endTime=${end}&limit=${chunkSize}`;
      }

      default:
        throw new Error(`Unknown data source: ${dataSource}`);
    }
  },

  // priceType에 따른 WebSocket URL 생성
  getWebSocketUrl: (symbol: string, interval: string) => {
    if (currentPriceType === 'last') {
      // Last price - kline 스트림
      return `wss://fstream.binance.com/ws/${symbol.toLowerCase()}@kline_${interval}`;
    } else if (currentPriceType === 'mark' || currentPriceType === 'index') {
      // Mark price - markPrice 스트림 (1초마다 업데이트)
      return `wss://fstream.binance.com/ws/${symbol.toLowerCase()}@markPrice@1s`;
    }

    // 기본값
    return `wss://fstream.binance.com/ws/${symbol.toLowerCase()}@kline_${interval}`;
  },

  async getBars(
    symbolInfo,
    resolution,
    periodParams,
    onHistoryCallback,
    onErrorCallback
  ) {
    try {
      const symbol = symbolInfo.name.replace('/', '').toUpperCase();
      const apiResolution = normalizeResolution(resolution);
      const dataSource: ChartDataSource = CHART_DATA_SOURCE;

      // console.log(`[CustomDatafeed] 데이터 소스: ${dataSource}, 심볼: ${symbol}`);

      // ═══════════════════════════════════════════════════════════════════════
      // 데이터 소스별 처리
      // ═══════════════════════════════════════════════════════════════════════
      switch (dataSource) {
        case 'backend': {
          // 🔧 백엔드 Chart API (UDF 형식 응답)
          const url = CustomDatafeed.getHistoryUrl(
            symbol,
            apiResolution,
            periodParams.from * 1000,
            periodParams.to * 1000,
            1000,
            'backend'
          );

          // console.log('[CustomDatafeed] 백엔드 API 호출:', url);

          const res = await fetch(url);
          const data = await res.json();

          // 에러/no_data 응답 처리
          if (data.s === 'error' || data.s === 'no_data') {
            if (data.s === 'error') {
              console.error('[CustomDatafeed] 백엔드 API 에러:', data.errmsg);
            }
            onHistoryCallback([], { noData: true });
            return;
          }

          // UDF 형식 → TradingView bar 형식 변환
          const bars = parseUDFResponse(data, resolution);
          onHistoryCallback(bars, { noData: bars.length === 0 });
          return;
        }

        case 'binance': {
          // 🔧 Binance Futures API 직접 호출 (폴백용)
          const resolutionToMs = {
            '1m': 60_000, '3m': 180_000, '5m': 300_000, '15m': 900_000,
            '30m': 1_800_000, '1h': 3_600_000, '2h': 7_200_000, '4h': 14_400_000,
            '6h': 21_600_000, '8h': 28_800_000, '12h': 43_200_000, '1d': 86_400_000,
            '3d': 259_200_000, '1w': 604_800_000, '1M': 2_592_000_000,
          };

          const chunkSizeMap = {
            '1m': 500, '3m': 500, '5m': 500, '15m': 400, '30m': 400,
            '1h': 300, '2h': 300, '4h': 250, '6h': 200, '8h': 200,
            '12h': 150, '1d': 120, '3d': 100, '1w': 80, '1M': 50,
          };

          const barMs = resolutionToMs[apiResolution];
          const chunkSize = chunkSizeMap[apiResolution] || 300;
          const to = periodParams.to * 1000;
          const from = Math.max(0, periodParams.from * 1000);

          const allBars: any[] = [];
          for (let cursor = from; cursor < to; cursor += barMs * chunkSize) {
            const end = Math.min(cursor + barMs * chunkSize, to);
            const url = CustomDatafeed.getHistoryUrl(
              symbol,
              apiResolution,
              cursor,
              end,
              chunkSize,
              dataSource
            );

            const res = await fetch(url);
            const data = await res.json();

            // Binance 응답 파싱
            const bars = parseBinanceResponse(data, resolution);

            allBars.push(...bars);
          }

          const uniqueSortedBars = Array.from(
            new Map(allBars.map((b) => [b.time, b])).values()
          )
            .sort((a, b) => a.time - b.time)
            .filter((bar) => bar.time >= from && bar.time <= to);

          onHistoryCallback(uniqueSortedBars, {
            noData: uniqueSortedBars.length === 0,
          });
          return;
        }

        default:
          throw new Error(`Unknown data source: ${dataSource}`);
      }
    } catch (err) {
      console.error('[CustomDatafeed] getBars error:', err);
      onErrorCallback(err);
    }
  },

  subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscribeUID, onResetCacheNeededCallback?) {
    // ✅ resetCacheCallback 저장
    if (onResetCacheNeededCallback) {
      resetCacheCallback = onResetCacheNeededCallback;

      // ✅ 전역 함수로 노출
      (window as any).__chartResetCache = () => {
        if (resetCacheCallback) {
          console.log('[CustomDatafeed] 캐시 초기화 호출');
          resetCacheCallback();
        }
      };
    }
    // const symbol = symbolInfo.name.replace('/', '').toUpperCase();
    // const interval = normalizeResolution(resolution);
    // const wsUrl = `wss://fstream.binance.com/ws/${symbol.toLowerCase()}@kline_${interval}`;
    //
    // if (binanceSockets[subscribeUID]) return;
    //
    // const ws = new WebSocket(wsUrl);
    //
    // ws.onmessage = (event) => {
    //   requestAnimationFrame(() => {
    //     try {
    //       const { k } = JSON.parse(event.data);
    //       const last = lastCloseMap[symbol];
    //
    //       if (last !== undefined && last === parseFloat(k.c)) return;
    //       lastCloseMap[symbol] = parseFloat(k.c);
    //
    //       onRealtimeCallback({
    //         time: k.t, open: +k.o, high: +k.h,
    //         low: +k.l, close: +k.c, volume: +k.v
    //       });
    //     } catch (err) {
    //       console.warn("Realtime data parse error:", err);
    //     }
    //   });
    // };
    //
    // ws.onerror = (e) => console.warn("WebSocket error:", e);
    // ws.onclose = () => console.log(`[WS Closed] ${symbol}@${interval}`);
    //
    // binanceSockets[subscribeUID] = ws;

    const symbol = symbolInfo.name.replace('/', '').toUpperCase();
    const interval = normalizeResolution(resolution);
    const wsUrl = CustomDatafeed.getWebSocketUrl(symbol, interval);

    if (binanceSockets[subscribeUID]) return;

    const ws = new WebSocket(wsUrl);
    binanceSockets[subscribeUID] = ws;

    let lastUpdate = 0;
    const MIN_UPDATE_INTERVAL = 100;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log(`[WS RESUME] ${symbol}@${interval}`);
        lastUpdate = 0;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    ws.onmessage = (event) => {
      const now = Date.now();
      if (now - lastUpdate < MIN_UPDATE_INTERVAL) return;
      lastUpdate = now;

      try {
        const data = JSON.parse(event.data);

        if (currentPriceType === 'last') {
          // Last price (kline) 데이터 처리
          const { k } = data;
          if (!k) {
            console.warn('[WebSocket] kline 데이터가 없음:', data);
            return;
          }

          const last = lastCloseMap[symbol];
          const close = parseFloat(k.c);
          if (last !== undefined && last === close) return;

          lastCloseMap[symbol] = close;

          onRealtimeCallback({
            time: k.t,
            open: +k.o,
            high: +k.h,
            low: +k.l,
            close: +k.c,
            volume: +k.v,
          });
        } else if (
          currentPriceType === 'mark' ||
          currentPriceType === 'index'
        ) {
          // Mark price 또는 Index price 데이터 처리
          const priceField = currentPriceType === 'mark' ? 'p' : 'i'; // mark: 'p', index: 'i'
          const price = parseFloat(data[priceField]);

          if (!price || isNaN(price)) {
            console.warn(
              `[WebSocket] ${currentPriceType} price 데이터가 유효하지 않음:`,
              data
            );
            return;
          }

          const last = lastCloseMap[symbol];
          if (last !== undefined && last === price) return;

          lastCloseMap[symbol] = price;

          // Mark/Index price는 단일 가격 데이터이므로 OHLC를 모두 같은 값으로 설정
          onRealtimeCallback({
            time: data.E || data.T || Date.now(), // 이벤트 시간 또는 타임스탬프
            open: price,
            high: price,
            low: price,
            close: price,
            volume: 0, // Mark/Index price는 볼륨이 없음
          });
        }
      } catch (e) {
        console.warn('[WebSocket] parse error:', e);
        console.warn('[WebSocket] 원본 데이터:', event.data);
      }
    };

    ws.onerror = (err) => {
      console.warn('[WebSocket] error:', err);
    };

    ws.onclose = () => {
      console.log(`[WS Closed] ${symbol}@${interval}`);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      delete binanceSockets[subscribeUID];
      delete lastCloseMap[symbol];
    };
  },

  unsubscribeBars(subscribeUID) {
    const ws = binanceSockets[subscribeUID];
    if (ws) {
      try {
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      } catch (e) {
        console.warn('WebSocket cleanup error:', e);
      }
      delete binanceSockets[subscribeUID];
    }
  },
};

function normalizeResolution(res: string): string {
  const map: Record<string, string> = {
    '1': '1m',
    '3': '3m',
    '5': '5m',
    '15': '15m',
    '30': '30m',
    '60': '1h',
    '120': '2h',
    '240': '4h',
    '360': '6h',
    '480': '8h',
    '720': '12h',
    '1D': '1d',
    '3D': '3d',
    '1W': '1w',
    '1M': '1M',
  };
  return map[res] || res;
}

// 백엔드 Chart API용 resolution 변환 (TradingView UDF 형식)
function normalizeToTVResolution(res: string): string {
  const map: Record<string, string> = {
    '1': '1',
    '3': '3',
    '5': '5',
    '15': '15',
    '30': '30',
    '60': '60',
    '120': '120',
    '240': '240',
    '360': '360',
    '480': '480',
    '720': '720',
    '1D': '1D',
    '3D': '3D',
    '1W': '1W',
    '1M': '1M',
    // 소문자 형식도 지원
    '1d': '1D',
    '3d': '3D',
    '1w': '1W',
    // Binance 형식도 지원
    '1m': '1',
    '3m': '3',
    '5m': '5',
    '15m': '15',
    '30m': '30',
    '1h': '60',
    '2h': '120',
    '4h': '240',
    '6h': '360',
    '8h': '480',
    '12h': '720',
  };
  return map[res] || res;
}

// ═══════════════════════════════════════════════════════════════════════════
// 응답 파싱 함수들
// ═══════════════════════════════════════════════════════════════════════════

/**
 * UDF 형식 응답 파싱 (백엔드, ByNova)
 * @param data UDF 응답 { s, t[], o[], h[], l[], c[], v[] }
 * @param resolution 타임프레임
 * @param isMilliseconds ByNova는 밀리초, 백엔드는 초 단위
 */
function parseUDFResponse(data: any, resolution: string, isMilliseconds = false): any[] {
  if (!data.t || !Array.isArray(data.t)) {
    return [];
  }

  return data.t.map((t: number, i: number) => {
    let barTime: number;

    // 일봉 시간 정규화: Daily bars need KST conversion (+9 hours)
    if (resolution === '1D' || resolution === 'D' || resolution === '1d') {
      const timestampSec = isMilliseconds ? t / 1000 : t;
      const adjustedTimestamp = (timestampSec + 9 * 3600) * 1000;
      barTime = Math.floor(adjustedTimestamp / 86400000) * 86400000;
    } else {
      barTime = isMilliseconds ? t : t * 1000; // 초 → 밀리초 변환
    }

    return {
      time: barTime,
      open: data.o[i],
      high: data.h[i],
      low: data.l[i],
      close: data.c[i],
      volume: data.v[i] || 0,
    };
  });
}

/**
 * Binance 배열 형식 응답 파싱
 * @param data Binance 응답 [[openTime, open, high, low, close, volume, ...], ...]
 * @param resolution 타임프레임
 */
function parseBinanceResponse(data: any[], resolution: string): any[] {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((d: any) => {
    let barTime: number;

    // 일봉 시간 정규화: Daily bars need KST conversion (+9 hours)
    if (resolution === '1D' || resolution === 'D' || resolution === '1d') {
      const adjustedTimestamp = (d[0] / 1000 + 9 * 3600) * 1000;
      barTime = Math.floor(adjustedTimestamp / 86400000) * 86400000;
    } else {
      barTime = d[0]; // Binance는 이미 밀리초
    }

    return {
      time: barTime,
      open: +d[1],
      high: +d[2],
      low: +d[3],
      close: +d[4],
      volume: +d[5],
    };
  });
}
